import { useCallback, useEffect, useMemo, useState } from 'react';
import { Dialog, Typography, CircularProgress, Select, MenuItem, FormControl } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { I18N_KEYS } from '@/language/keys';
import { color } from '@/colors';
import { ButtonContained } from '@/UI/Buttons/AppButton';
import { ZIndex } from '@/consts/z-indexes.consts';
import { FlexCenVer, FlexCol } from '@/UI/styles';
import { AppXBtn } from '@/UI/AppXBtn/AppXBtn';
import { TextSmRg, TextStdRg } from '@/UI/AppTypography/AppTypography';
import { CheckMarkIcon } from '@/UI/Icons/CheckMarkIcon';

interface CsvImportDialogProps {
  open: boolean;
  columns: string[];
  onClose: () => void;
  onSelect: (columns: string[]) => void | Promise<void>;
}

export const CsvImportDialog = ({ open, columns, onClose, onSelect }: CsvImportDialogProps) => {
  const { t } = useTranslation();
  const [selectedColumns, setSelectedColumns] = useState<string[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  // Reset selection when dialog closes
  useEffect(() => {
    if (!open) {
      setSelectedColumns([]);
    }
  }, [open]);

  const handleConfirm = useCallback(async () => {
    if (selectedColumns.length === 0) return;
    setIsLoading(true);
    try {
      await onSelect(selectedColumns);
    } finally {
      setIsLoading(false);
    }
  }, [selectedColumns, onSelect]);

  const handleClose = useCallback(
    (_event?: unknown, reason?: string) => {
      if (isLoading) return;
      if (reason === 'backdropClick') return;
      onClose();
    },
    [isLoading, onClose],
  );

  const selectedSet = useMemo(() => new Set(selectedColumns), [selectedColumns]);

  return (
    <Dialog
      onClose={handleClose}
      open={open}
      sx={{ zIndex: ZIndex.Overlays.CONFIRMATION_DIALOGS }}
      slotProps={{
        paper: {
          sx: {
            background: color.Black92,
            px: 4,
            pt: 6,
            pb: 4,
            position: 'relative',
            width: 420,
            borderRadius: 2,
          },
        },
      }}
    >
      <AppXBtn onClick={handleClose} sx={{ position: 'absolute', top: 12, right: 12 }} disabled={isLoading} />
      <Typography variant="body-lg-sb" sx={{ mb: 1 }}>
        {t(I18N_KEYS.CSV_COLUMN_DIALOG.TITLE)}
      </Typography>
      <TextStdRg sx={{ mb: 3 }} color={color.White64_T}>
        {t(I18N_KEYS.CSV_COLUMN_DIALOG.DESCRIPTION)}
      </TextStdRg>

      <FlexCol sx={{ gap: 3, mb: 4 }}>
        <FlexCol sx={{ gap: 1 }}>
          <TextSmRg color={color.White64_T}>{t(I18N_KEYS.CSV_COLUMN_DIALOG.COLUMN_LABEL)}</TextSmRg>
          <FormControl fullWidth size="small">
            <Select
              multiple
              value={selectedColumns}
              onChange={(e) => setSelectedColumns(e.target.value as string[])}
              disabled={isLoading}
              displayEmpty
              renderValue={(selected) => {
                if (selected.length === 0) {
                  return (
                    <span style={{ color: color.White40_T }}>{t(I18N_KEYS.CSV_COLUMN_DIALOG.COLUMN_PLACEHOLDER)}</span>
                  );
                }
                return selected.join(', ');
              }}
              MenuProps={{
                sx: {
                  zIndex: ZIndex.Overlays.CONFIRMATION_DIALOGS + 1,
                  '& .MuiPaper-root': {
                    bgcolor: color.Black92,
                    border: `1px solid ${color.White08_T}`,
                    borderRadius: 1,
                    mt: 1,
                  },
                  '& .MuiList-root': {
                    bgcolor: color.Black92,
                    p: 0.5,
                  },
                },
              }}
              sx={{
                bgcolor: color.Black92,
                color: color.White100,
                border: `1px solid ${color.White08_T}`,
                borderRadius: 1,
                '& .MuiSelect-icon': { color: color.White64_T },
                '& .MuiOutlinedInput-notchedOutline': { border: 'none' },
                '&:hover': { bgcolor: color.Black84 },
                '&.Mui-focused': { borderColor: color.White40_T },
              }}
            >
              {columns.map((col) => (
                <MenuItem
                  key={col}
                  value={col}
                  sx={{
                    bgcolor: color.Black92,
                    color: color.White100,
                    display: 'flex',
                    justifyContent: 'space-between',
                    '&:hover': { bgcolor: color.Black84 },
                    '&.Mui-selected': { bgcolor: color.Black92 },
                    '&.Mui-selected:hover': { bgcolor: color.Black84 },
                    '&.Mui-focusVisible': { bgcolor: color.Black92 },
                    '&.Mui-selected.Mui-focusVisible': { bgcolor: color.Black92 },
                  }}
                >
                  {col}
                  {selectedSet.has(col) && <CheckMarkIcon />}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </FlexCol>
      </FlexCol>

      <FlexCenVer sx={{ flexDirection: 'row-reverse', gap: 1 }}>
        <ButtonContained
          size="medium"
          onClick={() => void handleConfirm()}
          endIcon={isLoading ? <CircularProgress size={16} color="inherit" /> : null}
          disabled={selectedColumns.length === 0 || isLoading}
        >
          {t(I18N_KEYS.CSV_COLUMN_DIALOG.CONFIRM)}
        </ButtonContained>
        <ButtonContained size="medium" mode="text" onClick={handleClose} disabled={isLoading}>
          {t(I18N_KEYS.CSV_COLUMN_DIALOG.CANCEL)}
        </ButtonContained>
      </FlexCenVer>
    </Dialog>
  );
};
